import {useCallback, useMemo, useState, useRef} from "react";
import {useSelector} from "react-redux";
import {DashboardLayout} from "components/Layout";
import {BasicTable, SearchBox} from "components/Table";
import {isMobile} from "react-device-detect";
import {
	useDoctorPricesPerDoctor,
	useDeleteDoctorPrice,
	useAddDoctorPrice,
	useApproveDoctorPrice,
	useRejectDoctorPrice,
	useEditDoctorPrice,
} from "hooks/AdminHospital/DoctorPrice";
import DOCTOR_PRICE_STATUS from "config/doctorPriceStatus";
import {useToast} from "hooks/useToast";
import {convertErrorMessageFormat} from "utils/converter";
import {ModalConfirmation} from "components/Modal";
import {useHistory, useParams} from "react-router-dom";
import {convertToCurency} from "utils/converter";
import {ButtonAddTable} from "components/Button";
import clsx from "clsx";
import {ModalContainer} from "components/Modal";
import {Formik, FieldArray} from "formik";
import * as Yup from "yup";
import {useDetailStaffByUserID} from "hooks/SuperAdmin/Staff";

const FormSchemaValidation = Yup.object().shape({
	// doctorUserId: Yup.number().required("Dokter wajib di isi"),
	// hospitalId: Yup.mixed().required("Rumah sakit wajib di isi"),
	prices: Yup.array().of(
		Yup.object().shape({amount: Yup.number().required("Harga wajib di isi"), isDefault: Yup.boolean()}),
	),
});
const FormInitialValues = {
	doctorUserId: "",
	hospitalId: "",
	prices: [{amount: "", isDefault: false}],
};

const FormEditSchemaValidation = Yup.object().shape({
	price: Yup.string().required("Harga wajib di isi"),
	isDefault: Yup.boolean(),
});
const FormEditInitialValues = {
	price: "",
	isDefault: false,
};

export function PriceListPerDoctorPage() {
	const {id} = useParams();
	const history = useHistory();
	const selectedHospital = useSelector(state => state.HospitalsReducer.selectedHospital);
	const [dataLimit, setDataLimit] = useState(10);
	const [searchKeyword, setSearchKeyword] = useState("");
	const {
		data: prices,
		isLoading,
		filterPrices,
		setFilterPrices,
	} = useDoctorPricesPerDoctor(selectedHospital?.Hospital?.id, id);
	const {data: doctorDetail} = useDetailStaffByUserID(id);

	const statusStyle = useCallback(status => {
		switch (status) {
			case DOCTOR_PRICE_STATUS.WAITING:
				return {
					backgroundColor: "#FFF7E1",
					color: "#FB9C18",
					cursor: "pointer",
					textDecoration: "underline",
				};
			case DOCTOR_PRICE_STATUS.APPROVED:
				return {
					backgroundColor: "#DFFFDA",
					color: "#39B50E",
				};
			default:
				return {
					backgroundColor: "#FFD8D8",
					color: "#F21A1A",
				};
		}
	}, []);

	const {showToast} = useToast();
	const {mutate, isLoading: isDeletingData} = useDeleteDoctorPrice();
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [deleteItemData, setDeleteItemData] = useState({id: null, name: null});
	const handleDeleteData = () => {
		mutate(
			{id: deleteItemData.id},
			{
				onSuccess: () => {
					showToast("success", `Data harga ${deleteItemData?.doctor?.fullName} berhasil dihapus`, 3000);
					window.scrollTo(0, 0);
					setShowDeleteModal(false);
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	};

	const dataWithIDSerial =
		prices?.data?.rows?.map((item, index) => ({
			...item,
			idSerial: (prices?.data?.currentPage - 1) * dataLimit + index + 1,
		})) || [];

	const [isOpenAdd, setIsOpenAdd] = useState(false);
	const {mutate: mutateAdd} = useAddDoctorPrice();
	const formikRef = useRef();
	const handleAdd = useCallback(() => {
		const formData = {
			doctorUserId: id,
			hospitalId: selectedHospital?.Hospital?.id,
			prices: formikRef.current.values.prices,
		};

		mutateAdd(formData, {
			onSuccess: () => {
				showToast("success", "Data berhasil ditambahkan", 3000);
				setIsOpenAdd(false);
			},
			onError: res => {
				showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
			},
		});
	}, [id, mutateAdd, selectedHospital?.Hospital?.id, showToast]);

	const [isApproveOpen, setIsApproveOpen] = useState(false);
	const [idApprove, setIdApprove] = useState(null);
	const {mutate: mutateApprove} = useApproveDoctorPrice();
	const handleApprove = useCallback(() => {
		mutateApprove(
			{id: idApprove},
			{
				onSuccess: () => {
					showToast("success", "Data berhasil disetujui", 3000);
					setIsApproveOpen(false);
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	}, [idApprove, mutateApprove, showToast]);
	const {mutate: mutateReject} = useRejectDoctorPrice();
	const handleReject = useCallback(() => {
		mutateReject(
			{id: idApprove},
			{
				onSuccess: () => {
					showToast("success", "Data berhasil ditolak", 3000);
					setIsApproveOpen(false);
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	}, [idApprove, mutateReject, showToast]);

	const [isOpenEdit, setIsOpenEdit] = useState(false);
	const [editData, setEditData] = useState(null);
	const {mutate: mutateEdit, isLoading: isLoadingEdit} = useEditDoctorPrice();
	const formikRefEdit = useRef();
	const handleEdit = useCallback(() => {
		const formData = {
			id: editData?.id,
			price: formikRefEdit.current.values.price,
			isDefault: formikRefEdit.current.values.isDefault,
		};

		mutateEdit(formData, {
			onSuccess: () => {
				showToast("success", "Data berhasil ditambahkan", 3000);
				setIsOpenEdit(false);
			},
			onError: res => {
				showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
			},
		});
	}, [editData, mutateEdit, showToast]);

	const columns = useMemo(
		() => [
			{
				Header: "No",
				accessor: "idSerial",
			},
			{
				Header: "Harga",
				accessor: "consultationFee.amount",
				Cell: ({row}) => {
					return <>{convertToCurency(`${row.original.consultationFee?.amount}` || "0")}</>;
				},
			},
			{
				Header: "Status",
				accessor: "status",
				Cell: ({row}) => (
					<span
						class={`px-4 py-2 rounded-3`}
						style={statusStyle(row?.original?.status)}
						onClick={() => {
							if (row?.original?.status === DOCTOR_PRICE_STATUS.WAITING) {
								setIdApprove(row?.original?.id);
								setIsApproveOpen(true);
							}
						}}>
						{row?.original?.status}
					</span>
				),
			},
			{
				Header: "Aksi",
				accessor: "id",
				Cell: ({row}) => (
					<>
						<a
							class="text-orange wait-pay"
							style={{cursor: "pointer"}}
							onClick={() => {
								setEditData(row?.original);
								formikRefEdit.current?.setValues({
									isDefault: row?.original?.consultationFee?.isDefault || false,
								});
								setIsOpenEdit(true);
							}}>
							<span class="icon-ico-pencil ft-18" />
						</a>
						<a
							class="text-secondary wait-pay ms-2"
							style={{cursor: "pointer"}}
							onClick={() => {
								setDeleteItemData(row?.original);
								setShowDeleteModal(true);
							}}>
							<span class="icon-ico-delete ft-18" />
						</a>
					</>
				),
			},
		],
		[],
	);

	const columnsMobile = useMemo(
		() => [
			{
				Header: "No",
				accessor: "idSerial",
			},
			{
				Header: "Harga",
				accessor: "consultationFee.amount",
				Cell: ({row}) => {
					return <>{convertToCurency(`${row.original.consultationFee?.amount}` || "0")}</>;
				},
			},
			{
				Header: "Status",
				accessor: "status",
				Cell: ({row}) => (
					<span
						class={`px-4 py-2 rounded-3`}
						style={statusStyle(row?.original?.status)}
						onClick={() => {
							if (row?.original?.status === DOCTOR_PRICE_STATUS.WAITING) {
								setIdApprove(row?.original?.id);
								setIsApproveOpen(true);
							}
						}}>
						{row?.original?.status}
					</span>
				),
			},
			{
				Header: "Aksi",
				accessor: "id",
				Cell: ({row}) => (
					<>
						<a
							class="text-orange wait-pay"
							style={{cursor: "pointer"}}
							onClick={() => {
								setEditData(row?.original);
								formikRefEdit.current?.setValues({
									isDefault: row?.original?.consultationFee?.isDefault || false,
								});
								setIsOpenEdit(true);
							}}>
							<span class="icon-ico-pencil ft-18" />
						</a>
						<a
							class="text-secondary wait-pay ms-2"
							style={{cursor: "pointer"}}
							onClick={() => {
								setDeleteItemData(row?.original);
								setShowDeleteModal(true);
							}}>
							<span class="icon-ico-delete ft-18" />
						</a>
					</>
				),
			},
		],
		[],
	);

	return (
		<DashboardLayout>
			<>
				<ModalConfirmation
					approveLabel="Ya"
					description={`Apakah Anda yakin ingin menghapus data harga "Rp. ${convertToCurency(
						deleteItemData?.consultationFee?.amount?.toString() || "0",
					)}"?`}
					loading={isDeletingData}
					rejectLabel="Batal"
					visible={showDeleteModal}
					onApprove={handleDeleteData}
					onClose={() => setShowDeleteModal(false)}
					onReject={() => setShowDeleteModal(false)}
				/>
				<ModalConfirmation
					approveLabel="Approve"
					description={`Apa Anda ingin menyetujui harga ini??`}
					loading={isDeletingData}
					rejectLabel="Reject"
					title="Waiting Approve"
					visible={isApproveOpen}
					onApprove={handleApprove}
					onClose={() => {
						setIdApprove(null);
						setIsApproveOpen(false);
					}}
					onReject={handleReject}
				/>
				<div class="text-bold text-dark mb-4">
					<span class="icon-ico-back me-2" onClick={() => history.goBack()} />
					Detail
				</div>
				<SearchBox
					rightElement={
						<ButtonAddTable
							text="Request Harga Baru"
							onClick={() => {
								setIsOpenAdd(true);
							}}
						/>
					}
					onSearch={keyword => {
						setFilterPrices({...filterPrices, search: keyword, page: 1, limit: dataLimit});
						setSearchKeyword(keyword);
					}}
				/>

				<BasicTable
					columns={isMobile ? columnsMobile : columns}
					currentPage={prices?.data?.currentPage}
					data={dataWithIDSerial}
					limit={dataLimit}
					loading={isLoading}
					striped={true}
					title="Data Dokter"
					totalItems={prices?.data?.totalItems}
					totalPage={prices?.data?.totalPages}
					onLimitChange={limit => {
						setFilterPrices({...filterPrices, limit, page: 1, search: searchKeyword});
						setDataLimit(limit);
					}}
					onPageChange={selectedPage =>
						setFilterPrices({...filterPrices, page: selectedPage, limit: dataLimit, search: searchKeyword})
					}
				/>
				<ModalContainer visible={isOpenAdd} onClose={() => setIsOpenAdd(false)}>
					<div className="box-popup w-25" id="change-price">
						<div className="d-flex justify-content-between align-items-center mb-4">
							<div className="text-bold text-dark">Request Harga Baru</div>
							<div className="for-close">
								<button className="btn btn-close" type="button" onClick={() => setIsOpenAdd(false)} />
							</div>
						</div>

						<div className="popup-content">
							<Formik
								initialValues={FormInitialValues}
								innerRef={formikRef}
								validationSchema={FormSchemaValidation}
								onSubmit={handleAdd}>
								{({
									handleSubmit,
									handleBlur,
									handleChange,
									values,
									errors,
									touched,
									setFieldValue,
									setFieldTouched,
									resetForm,
								}) => (
									<>
										<div class="row gx-2 mb-5">
											<div class="col-12">
												<div class="setting-item">
													<label class="setting-label required">Nama Dokter</label>
													<input
														disabled
														class={clsx("form-control")}
														name="price"
														placeholder=""
														type="text"
														value={doctorDetail?.data?.fullName || ""}
													/>
												</div>
											</div>
											<div class="col-12">
												<label class="setting-label required">Harga</label>
												<FieldArray
													name="prices"
													render={arrayHelpers => (
														<>
															{values.prices && values.prices.length > 0 ? (
																values.prices.map((price, index) => (
																	<div
																		key={index}
																		className="w-100 d-flex align-items-center">
																		<div className="setting-item w-100 mb-3">
																			<input
																				class={clsx("form-control", {
																					"is-invalid":
																						touched.prices &&
																						touched.prices[index] &&
																						errors.prices &&
																						errors.prices[index] &&
																						errors.prices[index].amount,
																				})}
																				name={`prices.${index}.amount`}
																				placeholder="Masukkan harga"
																				type="number"
																				value={values.prices[index].amount}
																				onBlur={handleBlur}
																				onChange={handleChange}
																			/>
																			{touched.prices &&
																				touched.prices[index] &&
																				errors.prices &&
																				errors.prices[index] &&
																				errors.prices[index].amount && (
																					<div className="invalid-feedback d-block">
																						{errors.prices[index].amount}
																					</div>
																				)}
																		</div>

																		<div className="d-flex align-items-center ms-2">
																			<div class="form-check me-3">
																				<input
																					checked={
																						values.prices[index].isDefault
																					}
																					class="form-check-input"
																					id={`defaultPriceCheck${index}`}
																					name={`prices.${index}.isDefault`}
																					type="checkbox"
																					onChange={e => {
																						const isChecked =
																							e.target.checked;

																						setFieldValue(
																							`prices.${index}.isDefault`,
																							isChecked,
																						);
																						if (isChecked) {
																							values.prices.forEach(
																								(_, idx) => {
																									if (idx !== index) {
																										setFieldValue(
																											`prices.${idx}.isDefault`,
																											false,
																										);
																										setFieldTouched(
																											`prices.${idx}.isDefault`,
																											false,
																											false,
																										);
																									}
																								},
																							);
																						}
																					}}
																				/>
																				<label
																					class="form-check-label"
																					for={`defaultPriceCheck${index}`}>
																					Default
																				</label>
																			</div>
																		</div>
																		<button
																			class="btn btn-text text-orange p-1"
																			type="button"
																			onClick={() => {
																				arrayHelpers.insert(index + 1, {
																					amount: "",
																					isDefault: false,
																				});
																			}}>
																			<span class="icon-ico-plus ft-18" />
																		</button>
																		<button
																			class="btn btn-text p-1"
																			disabled={values.prices.length === 1}
																			type="button"
																			onClick={() => arrayHelpers.remove(index)}>
																			<span class="icon-ico-delete ft-18" />
																		</button>
																	</div>
																))
															) : (
																<div className="setting-item mb-3">Tidak ada harga</div>
															)}
														</>
													)}
												/>
											</div>
										</div>
										<div class="col-12 text-center">
											<button
												className="btn btn-orange-border r8 mx-2"
												type="button"
												onClick={() => {
													resetForm();
													setIsOpenAdd(false);
												}}>
												Kembali
											</button>
											<button
												className="btn btn-orange r8 mx-2"
												disabled={!FormSchemaValidation.isValidSync(values)}
												type="button"
												onClick={handleSubmit}>
												Ajukan
											</button>
										</div>
									</>
								)}
							</Formik>
						</div>
					</div>
				</ModalContainer>
				<ModalContainer visible={isOpenEdit} onClose={() => setIsOpenEdit(false)}>
					<div className="box-popup w-25" id="change-price">
						<div className="d-flex justify-content-between align-items-center mb-4">
							<div className="text-bold text-dark">Request Harga Baru</div>
							<div className="for-close">
								<button className="btn btn-close" type="button" onClick={() => setIsOpenEdit(false)} />
							</div>
						</div>

						<div className="popup-content">
							<Formik
								initialValues={FormEditInitialValues}
								innerRef={formikRefEdit}
								validationSchema={FormEditSchemaValidation}
								onSubmit={handleEdit}>
								{({
									handleSubmit,
									handleBlur,
									handleChange,
									values,
									errors,
									touched,
									setFieldValue,
									setFieldTouched,
									resetForm,
								}) => (
									<>
										<div class="row gx-2 mb-5">
											<div class="col-12">
												<div class="setting-item">
													<label class="setting-label required">Nama Dokter</label>
													<input
														disabled
														class={clsx("form-control")}
														value={doctorDetail?.data?.fullName || ""}
													/>
												</div>
											</div>
											<div class="col-12">
												<div class="setting-item">
													<label class="setting-label required">Harga saat ini</label>
													<input
														disabled
														class={clsx("form-control")}
														value={editData?.consultationFee?.amount || ""}
													/>
												</div>
											</div>
											<div class="col-12">
												<div class="setting-item">
													<label class="setting-label required">Request Harga</label>
													<input
														class={clsx(
															"form-control",
															errors?.quota && touched?.price && "form-control-error",
														)}
														disabled={isLoadingEdit}
														name="price"
														placeholder=""
														type="text"
														value={values?.price}
														onBlur={handleBlur}
														onChange={e => {
															const value = e.target.value.replace(/\D/g, "");

															setFieldValue("price", value);
														}}
													/>
													{errors.price && touched.price && (
														<p className="form-error-item-message">{errors.price}</p>
													)}
												</div>
											</div>
										</div>
										<div class="col-12 text-center">
											<button
												className="btn btn-orange-border r8 mx-2"
												type="button"
												onClick={() => {
													resetForm();
													setIsOpenEdit(false);
												}}>
												Kembali
											</button>
											<button
												className="btn btn-orange r8 mx-2"
												disabled={!FormSchemaValidation.isValidSync(values)}
												type="button"
												onClick={handleSubmit}>
												Ajukan
											</button>
										</div>
									</>
								)}
							</Formik>
						</div>
					</div>
				</ModalContainer>
			</>
		</DashboardLayout>
	);
}
